/**
 * What the arranger did to the piece, said along the bottom of the board.
 *
 * The arrangement hands the players the source's own notes -- each of its staves whole where
 * there is a player for every one, or else its tune, its bass and the notes between -- each at
 * the time the source wrote it, and changes nothing but an octave where a player cannot reach
 * a note. So what there is to say is who was given what, how much
 * of the piece each player waits through, and every source note that did not make it onto the
 * page and why. The counts add up: every note read is either written or named here under one
 * reason, because a listener who is not told will hear the gap and blame the arranger. Notes
 * of the chord added -- to fill in the harmony, by the AI arranger's plan or by the check -- a
 * counter-melody and notes varying the tune, where those were asked for, are the source's by no
 * reading, so they are counted apart, and who was given them
 * is named. Where the AI arranger was asked for and made no plan, the reason is said with
 * the notes on the source, since the rules then arranged the piece alone. Where the note model
 * wrote the parts under the tune (`under`), only the tune is the source's, whatever its add-ons
 * told it of the rest (`hints`): the model's notes are
 * the ones counted as added, and the source's other notes the ones replaced.
 *
 * The verdict stands beside how many of the notes read reached a player, and says how much it
 * graded ({@link graded}): a rate the writing checks had four chords to give is printed as
 * that, and one only the compass rule could give is not printed as a rate at all. Where the
 * check was asked for, its faults before and after stand beside the verdict, rule by rule in
 * the title, and where the note model wrote the parts, what they kept of the score and of
 * themselves (`ArrangeFollowed`).
 *
 * The numbers stay on the strip and the words go behind a disclosure. A status bar is read at a
 * glance between runs; a paragraph is read once. Nothing is hidden that changes what the
 * verdict means -- a loss or a problem lights the strip before it is opened. It is not opened
 * for the reader: almost every run leaves some note out, so a strip that opened itself did so
 * after nearly every run and took a third of the window from the score the run was for.
 */

import { useState } from "react";
import { handedIn, type ArrangeChecked, type KeptPayload } from "@app/shared-types";
import { given, graded, plural, sentence, trod } from "../lib/arrange-words";
import { count } from "../lib/format";
import ArrangeFollowed from "./ArrangeFollowed";
import { shownTo } from "./ArrangeHints";
import { Figure, Problems, Read, Told, WhoPlaysWhat } from "./ReadingParts";

export interface ArrangeReadingProps {
  readonly result: KeptPayload;
}

/** Every fault the checker counted, all rules together. */
function faults(counted: Record<string, number>): number {
  return Object.values(counted).reduce((sum, one) => sum + one, 0);
}

/** The check's faults rule by rule, and what it changed; see `ArrangeChecked`. */
function byRule({ before, after, moved, dropped, added }: ArrangeChecked): string {
  const rules = Object.keys(before)
    .filter((rule) => (before[rule] ?? 0) > 0 || (after[rule] ?? 0) > 0)
    .map((rule) => `${rule} ${count(before[rule] ?? 0)} → ${count(after[rule] ?? 0)}`);
  return `Faults the book's rules find, before the check and after it${rules.length === 0 ? ": none" : `: ${rules.join(", ")}`}. It moved ${count(moved)} of the arranger's own notes, left out ${count(dropped)} and gave waiting players ${count(added)} more, never changing a note of the source's.`;
}

export default function ArrangeReading({ result }: ArrangeReadingProps): JSX.Element {
  const { source, left, changed } = result;
  const hands = result.staves.filter((staff) => staff.plays === "right" || staff.plays === "left");
  // Whose the hands are, a pianist's or another's: a hand's staff names its instrument.
  const two = handedIn(hands.map((staff) => staff.instrument));
  const piano = two.length > 0;
  // More players of two hands than one: a hand is one of several, not of a pair.
  const several = hands.length > 2;
  const named = two.map((one) => one.label.toLowerCase()).join(" and the ");
  // An organist's pedal staff is listed under its hands, the lowest's left hand just over it.
  const pedals = result.staves.findIndex(trod);
  // A pedal staff held as its part wrote it names the part; one dealt the bass names none.
  const dealt = pedals >= 0 && result.staves[pedals].from === null;
  // A pianist alone is two hands; beside players, a note may have gone to either.
  const alone = piano && hands.length + (pedals < 0 ? 0 : 1) === result.staves.length;
  const who = alone ? "a hand" : piano ? "a player or a hand" : "a player";
  const Who = `${who.charAt(0).toUpperCase()}${who.slice(1)}`;
  // Whether the note model wrote the parts under the tune rather than the rules.
  const model = result.under !== null;
  // A bowed string or a guitar alone is written under its own tune, and stops what it can of it.
  const stops = model && !piano && result.staves.length === 1;
  // A guitar from the score stops the bass and the notes between as far as its left hand goes.
  const strums = result.staves.some((staff) => staff.strummed > 0);
  const losses: string[] = [];
  if (left.graces > 0) {
    losses.push(`${plural(left.graces, "grace note")}, which the score does not write`);
  }
  if (left.short > 0) {
    losses.push(`${plural(left.short, "note")} too short for the shortest notehead written`);
  }
  if (left.unisons > 0) {
    losses.push(
      `${plural(left.unisons, "note")} struck at the same moment and pitch as one already given to ${who}`,
    );
  }
  if (left.crowded > 0) {
    losses.push(
      strums
        ? `${plural(left.crowded, "inner note")} a guitar's left hand could not stop, with no player free to take them`
        : !piano
          ? `${plural(left.crowded, "inner note")} with no player free to take them; add a player to keep them`
          : alone
            ? `${plural(left.crowded, "inner note")} beyond what ${several ? "any" : "either"} hand can take: ${two.map((one) => one.reach).join(", or ")}, the hands not crossing`
            : `${plural(left.crowded, "inner note")} with no player free to take them that ${several ? "no" : "neither"} hand of the ${named} could take either`,
    );
  }
  if (left.unseated > 0) {
    losses.push(
      strums
        ? `${plural(left.unseated, "bass note")} a guitar's left hand could not stop, with no player for a bass`
        : `${plural(left.unseated, "bass note")}, the roster having no player left for a bass`,
    );
  }
  if (left.unplayable > 0) {
    losses.push(`${plural(left.unplayable, "note")} no player can reach in any octave`);
  }
  if (left.replaced > 0) {
    losses.push(
      `${plural(left.replaced, "note")} besides the tune, replaced by ${model ? "the parts the note model wrote under it" : "chords chosen for it"}`,
    );
  }
  const changes: string[] = [];
  if (changed.shortened > 0) {
    changes.push(
      `${plural(changed.shortened, "note")} cut short where the next note of the same line came in over them${piano || strums ? ", or where the hand could hold them no longer" : ""}`,
    );
  }
  if (changed.folded > 0) {
    changes.push(`${plural(changed.folded, "note")} moved an octave on their own to fit ${who}`);
  }
  if (changed.stopped > 0) {
    changes.push(
      `${plural(changed.stopped, "inner note")} played as the second note of a string player's double stop`,
    );
  }
  if (result.doubled > 0) {
    changes.push(
      alone
        ? `${plural(result.doubled, "note")} written a second time, the tune and the bass in octaves`
        : `${plural(result.doubled, "note")} written a second time, by players doubling a line`,
    );
  }

  if (result.added > 0) {
    const filled = result.staves.filter((staff) => staff.added > 0).map((staff) => staff.name);
    const checkedAdded = result.checked?.added ?? 0;
    changes.push(
      `${plural(result.added, "note")} ${model ? "written under the tune by the note model" : "of the chord added"}, which the original does not have${checkedAdded > 0 ? `, ${count(checkedAdded)} of them by the check to close a gap between the chord's notes` : ""}, given to ${filled.join(", ")}`,
    );
  }

  if (result.countered > 0) {
    const line = result.staves.find((staff) => staff.plays === "counter");
    changes.push(
      `${plural(result.countered, "note")} of a counter-melody, which the original does not have${line === undefined ? "" : `, played by ${line.name}`}`,
    );
  }
  if (result.varied > 0) {
    changes.push(
      `${plural(result.varied, "note")} varying the tune, which the original does not have, with ${plural(changed.trimmed, "note")} of the tune cut short to make room for them`,
    );
  }
  // Every note the arranger wrote of its own, whatever asked for it.
  const own = result.added + result.countered + result.varied;

  const notes = [...new Set(result.problems)];
  // What was read and done, and why an AI arranger asked for made no plan where it made none.
  const told =
    result.ai.unplanned === null
      ? source.notes
      : [
          ...source.notes,
          `the AI arranger made no plan, so the rules arranged alone: ${result.ai.unplanned}`,
        ];
  const speak = losses.length > 0 || notes.length > 0 || told.length > 0;

  const verdict = graded(result.verdict);
  const [open, setOpen] = useState(false);
  const checked = result.checked;

  return (
    <div className="shrink-0 border-t border-shell-edge bg-shell-bar">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-2.5 py-1.5">
        <Read source={source} />
        {own === 0 ? null : <Figure label="Added" value={count(own)} />}
        <Figure
          label="Written"
          value={`${count(result.written)} / ${count(source.read)}`}
          title={`${plural(result.written, "note")} of the ${count(source.read)} read reached ${who}`}
        />
        <Figure
          label="Verdict"
          value={verdict.value}
          tone={
            notes.length > 0 ? "text-fault" : verdict.judged === 0 ? "text-shell-dim" : "text-clean"
          }
          title={verdict.title}
        />
        {checked === null ? null : (
          <Figure
            label="Check"
            value={`${count(faults(checked.before))} → ${count(faults(checked.after))}`}
            tone={faults(checked.after) < faults(checked.before) ? "text-clean" : undefined}
            title={byRule(checked)}
          />
        )}
        {result.followed === null ? null : <ArrangeFollowed counts={result.followed} />}
        <WhoPlaysWhat open={open} speak={speak} onOpen={setOpen} />
      </div>

      {!open ? null : (
        <div className="max-h-[30vh] space-y-2 overflow-y-auto border-t border-shell-edge px-2.5 py-2 text-[11px] leading-relaxed text-shell-text">
          {model ? (
            <p>
              Only the tune is the original&apos;s, written as the score has it with its own tempo,
              dynamics, hairpins and phrasing. Every other part was written by the note model, which
              is shown {shownTo(result.hints, result.layers, result.strength, result.more)}, and
              then held to the book&apos;s rules by the check: those notes are counted below.
            </p>
          ) : (
            <p>
              Every note written is one the original plays, at the moment it plays it, with its own
              tempo, dynamics, hairpins and phrasing; only an octave changes, and only where {who}{" "}
              cannot reach a note. {Who} the music gives nothing to rests until it does
              {own === 0
                ? "."
                : ", except where it was asked to fill in the harmony, play a counter-melody or vary the tune, or the AI arranger or the check gave a waiting player a note of the chord: those notes are counted below."}
            </p>
          )}

          {result.key === null ? null : <p>· {sentence(result.key.said)}.</p>}

          <ul className="space-y-0.5">
            {result.staves.map((staff, at) => (
              <li key={staff.partId}>
                ·{" "}
                {given(
                  staff,
                  piano && !alone,
                  model,
                  stops,
                  piano && dealt && (at === pedals || at === pedals - 1),
                )}
              </li>
            ))}
          </ul>

          {losses.length === 0 ? (
            <p className="text-shell-dim">
              Every one of the {plural(source.read, "note")} read is written.
            </p>
          ) : (
            <div className="text-shell-dim">
              <p>
                {count(result.written)} of the {plural(source.read, "note")} read are written. Left
                out:
              </p>
              {losses.map((one) => (
                <p key={one}>· {one}</p>
              ))}
            </div>
          )}

          {changes.map((one) => (
            <p key={one} className="text-shell-dim">
              · {one}
            </p>
          ))}

          <Told told={told} />
          <Problems notes={notes} />
        </div>
      )}
    </div>
  );
}
